import { Info } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getWorkCategories } from '../../api/workApi'
import type { WorkCategoryInfo, WorkSubcategoryInfo, WorkWizardState } from '../../types/work'
import WorkPriceEstimate from './WorkPriceEstimate'
import WorkSubcategoryCard from './WorkSubcategoryCard'

interface WorkWizardStep2Props {
  state: WorkWizardState
  onChange: (patch: Partial<WorkWizardState>) => void
  onNext: () => void
  onBack: () => void
}

export default function WorkWizardStep2({ state, onChange, onNext, onBack }: WorkWizardStep2Props) {
  const [categories, setCategories] = useState<WorkCategoryInfo[]>([])

  useEffect(() => {
    getWorkCategories().then(setCategories).catch(() => {})
  }, [])

  const category = categories.find(c => c.id === state.category)
  const subcategories = category?.subcategories ?? []
  const selectedSub: WorkSubcategoryInfo | undefined = subcategories.find(s => s.id === state.subcategory)

  const canProceed = !!state.subcategory && state.title.trim().length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Subcategory grid */}
      {subcategories.length > 0 && (
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '10px' }}>
            Select Task Type
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {subcategories.map(sub => (
              <WorkSubcategoryCard
                key={sub.id}
                subcategory={sub}
                selected={state.subcategory === sub.id}
                onSelect={() => onChange({ subcategory: sub.id })}
              />
            ))}
          </div>
        </div>
      )}

      {/* File upload info banner */}
      {selectedSub && selectedSub.input_type === 'file' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'rgba(99, 102, 241, 0.08)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            fontSize: '12px',
            color: 'var(--text-secondary)',
          }}
        >
          <Info size={14} style={{ color: 'var(--primary, #6366f1)', flexShrink: 0 }} />
          You&apos;ll upload your file in the next step.
        </div>
      )}

      {/* Title */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
          Job Title <span style={{ color: 'var(--danger, #ef4444)' }}>*</span>
        </label>
        <input
          type="text"
          value={state.title}
          onChange={e => onChange({ title: e.target.value })}
          placeholder="e.g. Transcribe podcast episode 12"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '8px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Description */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
          Description <span style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)', fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          value={state.description}
          onChange={e => onChange({ description: e.target.value })}
          placeholder="Additional context or instructions..."
          rows={2}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '8px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
            fontSize: '13px',
            outline: 'none',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Input text (for text-based subcategories) */}
      {selectedSub && (selectedSub.input_type === 'text' || selectedSub.input_type === 'both') && (
        <div>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
            Input Text
          </label>
          <textarea
            value={state.input_text}
            onChange={e => onChange({ input_text: e.target.value })}
            placeholder={selectedSub.example_input}
            rows={5}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              lineHeight: 1.6,
            }}
          />
        </div>
      )}

      {/* Live price estimate */}
      {selectedSub && (
        <WorkPriceEstimate
          subcategory={selectedSub.id}
          inputText={state.input_text}
        />
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button
          onClick={onBack}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            background: 'transparent',
            border: '1px solid var(--border-color)',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          style={{
            padding: '10px 24px',
            borderRadius: '10px',
            background: canProceed ? 'var(--primary, #6366f1)' : 'var(--border-color)',
            color: canProceed ? '#fff' : 'var(--text-muted, #6b7280)',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: canProceed ? 'pointer' : 'not-allowed',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  )
}
